import type { PropsWithChildren } from 'react';
import { Children, createContext, useCallback, useContext, useMemo } from 'react';
import { AccordionGroup } from '../../Accordion';
import {
  tabLabelerByType,
  tabLabelerEnumerated,
  TabPanels,
  Tabs,
  TabSize,
  usePersistedTabs,
  useTabs,
  type PersistedDefaultTabProps,
  type TabProps,
  type UseTabsProps,
} from '../../Tabs';

export type EntityAdminTabProps = TabProps & {
  tabType?: string;
};
export const EntityAdminTabsContext = createContext<
  | {
      tabItems: PersistedDefaultTabProps<EntityAdminTabProps>[];
      selectedTab: EntityAdminTabProps;
      defaultInitialItems: EntityAdminTabProps[];
      onAdd: (tab: EntityAdminTabProps) => void;
      allowAddTabs: EntityAdminTabsWrapperProps['allowAddTabs'];
    }
  | undefined
>(undefined);
EntityAdminTabsContext.displayName = 'EntityAdminTabsContext';

/**
 * A hook to access the EntityAdminTabsContext.
 * @returns EntityAdminTabsContext if the component is wrapped in the EntityAdminTabsWrapper
 * @returns undefined if the component is not wrapped in the EntityAdminTabsWrapper
 */
export function useEntityAdminTabsContext() {
  return useContext(EntityAdminTabsContext);
}

const DEFAULT_ALL_TAB: PersistedDefaultTabProps<EntityAdminTabProps> = {
  id: 'default-all-tab',
  label: 'All',
  closable: true,
  editable: true,
};

interface EntityAdminTabsWrapperProps {
  tabItems?: PersistedDefaultTabProps<EntityAdminTabProps>[];
  persistKey?: string;
  allowAddTabs?: boolean;
}

const EntityAdminTabsAndContextWrapper = ({
  tabItems,
  persistKey,
  allowAddTabs = false,
  children,
}: PropsWithChildren<EntityAdminTabsWrapperProps>) => {
  const childrenArray: React.ReactNode[] = Children.toArray(children);

  const defaultInitialItems: PersistedDefaultTabProps<TabProps>[] = useMemo(() => {
    //if items in tabItems does not have tabType, fallback to its label
    return tabItems ?? [DEFAULT_ALL_TAB];
  }, [tabItems]);

  if (allowAddTabs && !persistKey) {
    throw new Error('persistKey must be provided when allowAddTabs is true');
  }

  if (!allowAddTabs && !defaultInitialItems) {
    throw new Error('staticTabItems must be provided when allowAddTabs is false');
  }

  const persistedTabs = usePersistedTabs<TabProps>(persistKey, { defaultInitialItems });

  const tabLabeler: UseTabsProps<EntityAdminTabProps>['tabLabeler'] = tabLabelerByType<EntityAdminTabProps, 'tabType'>({
    typeKey: 'tabType',
    typeToLabel: item => item ?? 'All',
  });

  const tabLabelerEnum = tabLabelerEnumerated('All');

  const tabs = useTabs<TabProps>({
    ...(persistKey ? persistedTabs : { initialItems: defaultInitialItems }),
    showAddTab: allowAddTabs,
    allowClosingLastTab: false,
    tabLabeler: tabItems ? tabLabeler : tabLabelerEnum,
  });

  const renderCorrectTab = useCallback(
    (tab: EntityAdminTabProps) => {
      if (childrenArray.length === 1) {
        // Default behavior - Accordion Group handles which page to show
        return childrenArray.at(0);
      } else {
        // Custom static tabs behavior - Show the correct page based on the tabType
        const foundIndex = defaultInitialItems.findIndex(item => item.label === tab.tabType);
        if (foundIndex === -1) {
          throw new Error(`Tab with label ${tab.tabType} not found in tabItems`);
        }
        return childrenArray[foundIndex];
      }
    },
    [childrenArray, defaultInitialItems]
  );

  return (
    <EntityAdminTabsContext.Provider
      value={{
        tabItems: tabs.items as PersistedDefaultTabProps<EntityAdminTabProps>[],
        selectedTab: tabs.items[tabs.selectedIndex],
        defaultInitialItems,
        allowAddTabs,
        onAdd: tabs.onAdd,
      }}
    >
      <Tabs {...tabs} h="100%" size={TabSize.Large} data-testid="entity-admin-tabs">
        <TabPanels h="100%">
          {tabs.items.map(tab => (
            <AccordionGroup key={tab.id}>{renderCorrectTab(tab)}</AccordionGroup>
          ))}
        </TabPanels>
      </Tabs>
    </EntityAdminTabsContext.Provider>
  );
};

/**
 * Wrap the entire EntityAdmin component in this wrapper to enable the tabs context for this page.
 * @param tabItems - An array of TabProps to be used as tabs. If undefined, a default "All" tab will be created.
 */
export const EntityAdminTabsWrapper = EntityAdminTabsAndContextWrapper;
